import { useQuery, useIsFetching } from '@tanstack/react-query'
import { map } from 'lodash'
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'
import { ReservationListItem } from '@nxcr-org/web-api-interface/lib/subscription_service_pb'
import { ReservationService } from '../../api/reservation-service'
import { useContext } from 'react'
import { ClientContext } from '../../../../utils/clientContext'
import {GridPaginationModel} from '@mui/x-data-grid'

export function useReservationsTable() {
  const { clientId } = useContext(ClientContext)
  const [{ page, rowsPerPage, search, programId }, setQueryParams] =
    useQueryParams({
      search: StringParam,
      page: withDefault(NumberParam, 0),
      rowsPerPage: withDefault(NumberParam, 100),
      programId: withDefault(StringParam, 'all'),
    })
  // note: we should useQuery here later on when we move to NexusService?
  const setPaginationModel = (newPaginationModel: GridPaginationModel) => {
    setQueryParams({
      page: newPaginationModel.page,
      rowsPerPage: newPaginationModel.pageSize,
    })
  }

  const setQuery = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setQueryParams({ search: evt.target.value })
  }

  const setProgram = (newProgramId: string) => {
    setQueryParams({ programId: newProgramId ? newProgramId : 'all' })
  }

  const isFetching = useIsFetching()

  const { data: programs } = useQuery({
    queryKey: ['/listPrograms', clientId],
    queryFn: () => {
      return ReservationService.listPrograms(clientId)
    },
      initialData: {
        programsList: [],
      },
      enabled: !!clientId,
    }
  )

  const { data: reservations } = useQuery({
    queryKey: ['/reservations', { search, page, rowsPerPage, programId, clientId }],
    queryFn: async () => {
      const res = await ReservationService.getReservations({
        // calculate the index of the last item on the requested page
        pageState: (rowsPerPage * page).toString(10),
        pageSize: rowsPerPage,
        programId,
        query: search,
        clientId,
      })
      return {
        total: res.total,
        reservations: createReservationRows(res.reservationsList),
      }
    },
    placeholderData: (previousData) => previousData,
    }
  )

  return {
    total: reservations?.total || 0,
    page,
    setPaginationModel,
    rowsPerPage,
    reservations: reservations?.reservations || [],
    setQuery,
    setProgram,
    search,
    programs: programs || { programsList: [] },
    programId,
    isLoading: isFetching > 0,
  }
}

function createReservationRows(reservations: ReservationListItem.AsObject[]) {
  return map(reservations, (reservation) => {
    const customerName = `${reservation?.firstName} ${reservation?.lastName}`
    const customerId = `${reservation?.customerId}`
    return {
      id: reservation.reservationId,
      accountNumber: reservation.accountNumber,
      customerId,
      customerName,
      make: reservation.make,
      model: reservation.model,
      isPending: false,
      status: reservation.status,
      reservationId: reservation.reservationId,
      subscriptionId: reservation.customerId,
      waitlistStart: reservation.waitlistStart,
      color: reservation.color || 'N/A',
      zipcode: reservation.postal,
    }
  })
}
